import { useRef, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, FileText, Upload } from 'lucide-react'
import { uuidv7 } from '@one-deployment/shared'
import type { IntakeFile, IntakeRun, IntakeRunList } from '@one-deployment/api'
import { Badge, Button, Card, PageHeader, SkeletonRows, Textarea } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { Age, EmptyState, ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { ApiError, enc, get, post } from '@/lib/api'

const MAX_BYTES = 20 * 1024 * 1024
const runsKey = (project: string) => ['intake', project] as const

/** One file straight to storage; the run that uses it is created after. */
async function upload(project: string, f: File): Promise<IntakeFile> {
  const res = await fetch(`/api/projects/${enc(project)}/intake/files`, {
    method: 'POST', credentials: 'same-origin', body: f,
    headers: { 'idempotency-key': uuidv7(), 'x-file-name': encodeURIComponent(f.name), 'content-type': f.type || 'application/octet-stream', accept: 'application/json' },
  })
  const body = await res.json().catch(() => null) as { error?: { code: string; message: string } } | IntakeFile | null
  if (!res.ok) throw new ApiError(res.status, (body as { error?: { code: string } })?.error?.code ?? `http_${res.status}`, (body as { error?: { message: string } })?.error?.message ?? 'Upload failed')
  return body as IntakeFile
}

const STATUS: Record<IntakeRun['status'], { label: string; tone: 'info' | 'ok' | 'warn' | 'muted' }> = {
  creating: { label: 'creating the intake ticket', tone: 'info' },
  splitting: { label: 'Claude is reading it', tone: 'info' },
  done: { label: 'done', tone: 'ok' },
  failed: { label: 'failed', tone: 'warn' },
  undone: { label: 'undone', tone: 'muted' },
}

/** Tickets from text or documents (features/08). */
export function IntakePage() {
  const { project } = useParams({ strict: false }) as { project: string }
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const runs = useQuery({
    queryKey: runsKey(project),
    queryFn: ({ signal }) => get<IntakeRunList>(`/api/projects/${enc(project)}/intake`, signal),
    // Poll while a run is still working; Linear and the box answer in their own time.
    refetchInterval: (q) => (q.state.data?.runs.some((r) => r.status === 'creating' || r.status === 'splitting') ? 3000 : false),
  })
  const start = useMutation({
    mutationFn: async () => {
      const stored = []
      for (const f of files) stored.push(await upload(project, f))
      return post<IntakeRun>(`/api/projects/${enc(project)}/intake`, { text, files: stored })
    },
    onSuccess: () => { setText(''); setFiles([]); setError(null); void qc.invalidateQueries({ queryKey: runsKey(project) }) },
    onError: (e) => setError(e instanceof ApiError ? (e.what ?? e.message) : String(e)),
  })
  const undo = useMutation({
    mutationFn: (id: string) => post<IntakeRun>(`/api/projects/${enc(project)}/intake/${enc(id)}/undo`),
    onSettled: () => void qc.invalidateQueries({ queryKey: runsKey(project) }),
  })
  const addFiles = (list: FileList | null) => {
    const picked = [...(list ?? [])]
    const big = picked.find((f) => f.size > MAX_BYTES)
    if (big) { setError(`${big.name} is over 20 MB`); return }
    setFiles((cur) => [...cur, ...picked].slice(0, 10))
  }

  return (
    <div>
      <PageHeader title="Tickets from text" meta="Paste notes, an email or a call summary, or add documents. Claude makes the tickets in Linear, triaged, and adds to existing tickets instead of duplicating them." />
      <div className="grid gap-4 px-6 py-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <RoleGate action="ticket.intake" project={project} fallback={<EmptyState title="Read-only" body="A developer or lead can make tickets here." />}>
          <Card title="Source">
            <div className="flex flex-col gap-3 p-3">
              <Textarea rows={14} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste text here" aria-label="Source text" />
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <input ref={input} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = '' }} />
                <Button variant="secondary" size="sm" onClick={() => input.current?.click()}><Upload size={13} /> Add files</Button>
                {files.map((f, i) => (
                  <span key={`${f.name}-${i}`} className="flex items-center gap-1 rounded border border-border px-2 py-0.5">
                    <FileText size={12} />{f.name}
                    <SButton variant="ghost" size="icon-xs" aria-label={`Remove ${f.name}`} className="size-4 rounded-xs text-fg-faint hover:bg-transparent hover:text-fg" onClick={() => setFiles(files.filter((_, j) => j !== i))}>×</SButton>
                  </span>
                ))}
              </div>
              {error && <div role="alert" className="text-xs text-err">{error}</div>}
              <div><Button disabled={start.isPending || (!text.trim() && files.length === 0)} onClick={() => start.mutate()}>{start.isPending ? 'Sending…' : 'Make tickets'}</Button></div>
            </div>
          </Card>
        </RoleGate>
        <Card title="Recent">
          {runs.error && !runs.data ? <ErrorState error={runs.error} retry={() => void runs.refetch()} /> : !runs.data ? <SkeletonRows rows={4} /> : runs.data.runs.length === 0 ? (
            <EmptyState title="Nothing yet" body="Runs appear here with the tickets they made." />
          ) : (
            <ul>{runs.data.runs.map((r) => <RunRow key={r.id} run={r} project={project} onUndo={() => undo.mutate(r.id)} undoing={undo.isPending && undo.variables === r.id} />)}</ul>
          )}
        </Card>
      </div>
    </div>
  )
}

function RunRow({ run: r, project, onUndo, undoing }: { run: IntakeRun; project: string; onUndo: () => void; undoing: boolean }) {
  return (
    <li className="border-b border-border px-3 py-2 text-sm last:border-0">
      <div className="flex items-center gap-2">
        <span className="flex-1 truncate font-medium">{r.title}</span>
        <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
        <Age from={r.createdAt} className="text-xs text-fg-faint" />
      </div>
      <div className="mt-0.5 flex items-center gap-2 text-xs text-fg-subtle">
        <span>{r.createdBy}</span>
        {r.summary && <span>· {r.summary}</span>}
        {r.issue && <a href={r.issue.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-fg">· {r.issue.identifier}<ExternalLink size={11} /></a>}
        {r.issue?.stage === 'needs_human' && <span className="text-warn">· stopped: {r.issue.reason}</span>}
        {r.undoUntil && <Button variant="ghost" size="sm" className="ml-auto" disabled={undoing} onClick={onUndo}>Undo</Button>}
      </div>
      {r.items.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-xs">
          {r.items.map((i, n) => (
            <li key={n} className="flex items-center gap-2">
              <span className="w-16 font-mono text-fg-subtle">{i.identifier ?? '…'}</span>
              <span className="flex-1 truncate">{i.title}</span>
              {i.duplicateOf ? <Badge>added to {i.duplicateOf}</Badge> : i.kind && <Badge tone="accent">{i.kind.replace('_', ' ')}</Badge>}
              {i.held && <Badge tone="warn">held</Badge>}
              {i.url && <a href={i.url} target="_blank" rel="noreferrer" aria-label="Open in Linear"><ExternalLink size={11} /></a>}
            </li>
          ))}
        </ul>
      )}
      {r.status === 'done' && r.items.some((i) => !i.duplicateOf) && (
        <Link to="/p/$project/issues" params={{ project }} className="mt-1 inline-block text-xs text-accent hover:underline">See them in Issues</Link>
      )}
    </li>
  )
}
